import { useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";
import Reveal from "../Reveal";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";

export default function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Message received. We respond within 24 hours.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-obsidian border border-white/10 px-5 py-4 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-volt/60 transition-colors duration-300";

  return (
    <section id="contact" className="py-28 lg:py-40 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-16">
        <Reveal>
          <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-volt mb-4">04 / Contact</p>
          <h2 className="font-display font-bold tracking-tight text-2xl sm:text-4xl lg:text-5xl leading-tight">
            Ready to stop <br />competing and <br /><span className="text-volt">start dominating?</span>
          </h2>
          <p className="mt-6 text-slate-400 text-base md:text-lg max-w-md leading-relaxed">
            Open a client account to get your unique Zork ID, submit project briefs and track everything from your
            private portal. Access is provisioned personally by the studio — request your credentials below.
          </p>
          <button
            data-testid="contact-cta-portal"
            onClick={() => navigate(user ? "/dashboard" : "/login")}
            className="group mt-10 inline-flex items-center gap-2 border border-volt/50 text-volt font-mono text-xs uppercase tracking-[0.2em] px-8 py-4 hover:bg-volt hover:text-obsidian transition-colors duration-300"
          >
            Enter Client Portal
            <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </Reveal>

        <Reveal delay={0.15}>
          <form onSubmit={submit} data-testid="contact-form" className="border border-white/5 bg-panel/60 p-8 lg:p-10 space-y-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">Direct line — no account needed</p>
            <input data-testid="contact-name-input" required value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} />
            <input data-testid="contact-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="Email address" className={inputCls} />
            <textarea data-testid="contact-message-input" required rows={5} value={form.message} onChange={set("message")} placeholder="Tell us about the mission..." className={`${inputCls} resize-none`} />
            <button
              data-testid="contact-submit-btn"
              disabled={sending}
              className="w-full bg-volt text-obsidian font-mono text-xs uppercase tracking-[0.2em] font-bold px-8 py-4 hover:bg-[#CCEE00] transition-colors duration-300 disabled:opacity-50"
            >
              {sending ? "Transmitting..." : "Send Transmission"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
